import React, { FC, useEffect, useRef } from "react";
import { Property } from "shared-ts-property";
import {
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  RadioGroupProps,
  Stack,
  Typography,
} from "@mui/material";
import { SweepDueStatusProperty } from "./SweepDueStatusProperty";
import {
  resolveSootingDegreeExplanation,
  WasteAmountUnits,
} from "firefly-shared-ts-domain";
import { PropertiesCard } from "shared-ts-ui-properties-card";
import { useTranslation } from "react-i18next";
import { SweepingFormValues } from "./SweepingFormValues";
import { FormikErrors, FormikTouched } from "formik";
import { FormikHelpers } from "formik/dist/types";
import { DuenessStringsEnum } from "shared-ts-types";
import { WastePieChart } from "./WastePieChart";
import { notNull } from "shared-ts-utils-core";
import { PersistedState } from "./PersistedState";
import { FormSteps } from "shared-ts-form";

export interface SweepingPropertiesEditProps {
  persistedState: PersistedState;
  values: SweepingFormValues;
  errors: FormikErrors<SweepingFormValues>;
  touched: FormikTouched<SweepingFormValues>;
  dirty: boolean;
  setValues: FormikHelpers<SweepingFormValues>["setValues"];
  setFieldValue: FormikHelpers<SweepingFormValues>["setFieldValue"];
  setFieldTouched: FormikHelpers<SweepingFormValues>["setFieldTouched"];
  onDirtyChange: (value: boolean) => void;
}

export const SweepingPropertiesEdit: FC<SweepingPropertiesEditProps> = (
  props
) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setValues,
    setFieldTouched,
    onDirtyChange,
  } = props;
  const mode = "edit";
  const { t } = useTranslation("firefly-chimney-insights-ts-domain");
  const { t: tFireflySharedDomain } = useTranslation(
    "firefly-shared-ts-domain"
  );
  const timePropertyInputRef = useRef<HTMLInputElement>(null);

  const handleSootingDegreeChange = (value: number | null | undefined) => {
    const valuesToSet: SweepingFormValues = { ...values };
    valuesToSet["sootingDegree"] = value ?? null;
    if ((value ?? 0) < 100) {
      valuesToSet["dueness"] = DuenessStringsEnum.NotDue;
    } else if ((value ?? 0) === 100) {
      valuesToSet["dueness"] = DuenessStringsEnum.Due;
    } else if ((value ?? 0) > 100) {
      valuesToSet["dueness"] = DuenessStringsEnum.Overdue;
    }
    setValues(valuesToSet);
  };

  const handleWasteAmountChange: RadioGroupProps["onChange"] = (
    event,
    value
  ) => {
    const wasteAmount = value === "true";
    const valuesToSet: SweepingFormValues = { ...values };
    if (!wasteAmount) {
      valuesToSet["wasteAmountValue"] = null;
      valuesToSet["wasteComposition"] = false;
      valuesToSet["creosoteAmountValue"] = null;
      valuesToSet["sootAmountValue"] = null;
      valuesToSet["ashAmountValue"] = null;
    }

    valuesToSet["wasteAmount"] = wasteAmount;
    setValues(valuesToSet);
  };

  const handleWasteCompositionChange: RadioGroupProps["onChange"] = (
    event,
    value
  ) => {
    const wasteComposition = value === "true";
    const valuesToSet: SweepingFormValues = { ...values };
    if (!wasteComposition) {
      valuesToSet["creosoteAmountValue"] = null;
      valuesToSet["sootAmountValue"] = null;
      valuesToSet["ashAmountValue"] = null;
    }
    valuesToSet["wasteComposition"] = wasteComposition;
    setValues(valuesToSet);
  };

  const handleSootAmountValueChange = (value: number | null | undefined) => {
    const total = (value ?? 0) + (values.creosoteAmountValue ?? 0);
    if (total <= 100) {
      const valuesToSet: SweepingFormValues = { ...values };
      valuesToSet["sootAmountValue"] = value ?? null;
      const newAshAmountValue =
        100 - (value ?? 0) - (values.creosoteAmountValue ?? 0);
      if (newAshAmountValue >= 0) {
        valuesToSet["ashAmountValue"] = newAshAmountValue;
      }
      setValues(valuesToSet);
    }
  };
  const handleCreosoteAmountChange = (value: number | null | undefined) => {
    const total = (value ?? 0) + (values.sootAmountValue ?? 0);
    if (total <= 100) {
      const valuesToSet: SweepingFormValues = { ...values };
      valuesToSet["creosoteAmountValue"] = value ?? null;
      const newAshAmountValue =
        100 - (value ?? 0) - (values.sootAmountValue ?? 0);
      if (newAshAmountValue >= 0) {
        valuesToSet["ashAmountValue"] = newAshAmountValue;
      }
      setValues(valuesToSet);
    }
  };

  useEffect(() => {
    onDirtyChange(props.dirty);
  }, [onDirtyChange, props.dirty]);

  useEffect(() => {
    timePropertyInputRef.current?.focus();
  }, [mode]);

  return (
    <PropertiesCard
      header={{
        title: t("Sweep"),
      }}
      slotProps={{
        CardHeader: {
          larger: true,
        },
        CardContent: {
          sx: {
            gap: 3,
          },
        },
      }}
      direction={"column"}
      elevation={0}
      disableBorder={true}
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <FormSteps
        showAllSteps={props.persistedState !== "new"}
        stackProps={{ sx: { gap: 3 } }}
        steps={[
          {
            content: (
              <Property
                name={"time"}
                label={t("Date")}
                value={values.time}
                disableFuture={true}
                type={"date"}
                mode={mode}
                inputRef={timePropertyInputRef}
                error={
                  touched.time === true &&
                  touched.sootingDegree &&
                  errors.time != null
                }
                helperText={touched.time && errors.time}
                onFormikFieldValueChange={setFieldValue}
                onBlur={() => setFieldTouched("time", true)}
              />
            ),
          },
          {
            when: touched.time && values.time != null,
            content: (
              <>
                <Typography>
                  {tFireflySharedDomain("Sweeper’s Assessments")}
                </Typography>
                <Stack
                  direction={"row"}
                  useFlexGap
                  sx={{
                    gap: 2,
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Property<number>
                    name={"sootingDegree"}
                    label={tFireflySharedDomain(
                      "Assessment of Actual Sooting Degree"
                    )}
                    mode={mode}
                    fullWidth
                    editComponent={"Slider"}
                    editComponentProps={{
                      Slider: {
                        min: 0,
                        max: 125,
                        step: 25,
                      },
                    }}
                    value={values.sootingDegree}
                    unit={"%"}
                    error={
                      touched.sootingDegree && errors.sootingDegree != null
                    }
                    helperText={
                      errors.sootingDegree != null
                        ? errors.sootingDegree
                        : notNull(values.sootingDegree, (it) =>
                            resolveSootingDegreeExplanation(
                              it,
                              tFireflySharedDomain,
                              false
                            )
                          )
                    }
                    onFormikFieldValueChange={(name, value) => {
                      setFieldValue(name, value);
                      handleSootingDegreeChange(value);
                    }}
                    onBlur={() => setFieldTouched("sootingDegree", true)}
                    sx={{ width: "222px" }}
                  />
                  {values.dueness != null && (
                    <SweepDueStatusProperty
                      label={tFireflySharedDomain(
                        "Assessment of the Need for Sweep"
                      )}
                      propertyMode={"read"}
                      value={values.dueness}
                    />
                  )}
                </Stack>
              </>
            ),
          },
          {
            when:
              touched.sootingDegree &&
              values.sootingDegree != null &&
              values.sootingDegree > 0,
            content: (
              <>
                <FormControl
                  error={touched.wasteAmount && errors.wasteAmount != null}
                >
                  <FormLabel id="wasteAmount">
                    {t(
                      "Where you able to measure the amount of waste after the sweeping?"
                    )}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="wasteAmount"
                    name="wasteAmount"
                    value={values.wasteAmount}
                    onChange={handleWasteAmountChange}
                    onBlur={() => setFieldTouched("wasteAmount", true)}
                    sx={{ pl: 1 }}
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label={t("Yes")}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label={t("No")}
                    />
                  </RadioGroup>
                  {
                    <FormHelperText
                      error={touched.wasteAmount && errors.wasteAmount != null}
                    >
                      {errors.wasteAmount}
                    </FormHelperText>
                  }
                </FormControl>
              </>
            ),
          },
          {
            visibleWhen: values.wasteAmount === true,
            when: touched.wasteAmount || values.wasteAmount === true,
            content: (
              <>
                <Divider />
                <Typography>{t("Waste")}</Typography>
                <Stack
                  direction={"row"}
                  sx={{ justifyContent: "space-between" }}
                >
                  <Stack useFlexGap sx={{ gap: 2 }}>
                    <Property<number>
                      name={"wasteAmountValue"}
                      label={t("Amount of waste")}
                      value={values.wasteAmountValue}
                      type={"number"}
                      unit={values.wasteAmountUnit}
                      units={WasteAmountUnits}
                      mode={mode}
                      onFormikFieldValueChange={setFieldValue}
                      onUnitChange={(value) => {
                        setFieldValue("wasteAmountUnit", value);
                      }}
                      error={
                        touched.wasteAmountValue &&
                        errors.wasteAmountValue != null
                      }
                      helperText={errors.wasteAmountValue}
                      onBlur={() => setFieldTouched("wasteAmountValue", true)}
                    />
                  </Stack>
                </Stack>
              </>
            ),
          },
          {
            visibleWhen:
              values.wasteAmount === true && (values.wasteAmountValue ?? 0) > 0,
            when: values.wasteAmountValue != null && touched.wasteAmountValue,
            content: (
              <>
                <FormControl
                  error={
                    touched.wasteComposition && errors.wasteComposition != null
                  }
                >
                  <FormLabel id="wasteComposition">
                    {t("Can you specify the composition of the waste?")}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="wasteComposition"
                    name="wasteComposition"
                    value={values.wasteComposition}
                    onChange={handleWasteCompositionChange}
                    onBlur={() => setFieldTouched("wasteComposition", true)}
                    sx={{ pl: 1 }}
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label={t("Yes")}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label={t("No")}
                    />
                  </RadioGroup>
                  {
                    <FormHelperText
                      error={
                        touched.wasteComposition &&
                        errors.wasteComposition != null
                      }
                    >
                      {errors.wasteComposition}
                    </FormHelperText>
                  }
                </FormControl>
              </>
            ),
          },
          {
            visibleWhen:
              values.wasteComposition === true &&
              (values.wasteAmountValue ?? 0) > 0,
            when: values.wasteComposition === true,
            content: (
              <Stack direction={"row"}>
                <Stack useFlexGap sx={{ gap: 2 }}>
                  <Property<number>
                    name={"creosoteAmountValue"}
                    label={t("Creosote")}
                    type={"number"}
                    value={values.creosoteAmountValue}
                    unit={values.creosoteAmountUnit}
                    editComponent={"Slider"}
                    editComponentProps={{
                      Slider: { min: 0, max: 100, step: 10 },
                    }}
                    mode={mode}
                    onFormikFieldValueChange={(name, value) => {
                      handleCreosoteAmountChange(value);
                    }}
                    error={
                      touched.creosoteAmountValue &&
                      errors.creosoteAmountValue != null
                    }
                    helperText={errors.creosoteAmountValue}
                    onBlur={() => setFieldTouched("creosoteAmountValue", true)}
                  />

                  <Property<number>
                    name={"sootAmountValue"}
                    label={t("Soot")}
                    type={"number"}
                    value={values.sootAmountValue}
                    inputProps={{
                      min: 0,
                    }}
                    unit={values.sootAmountUnit}
                    editComponent={"Slider"}
                    editComponentProps={{
                      Slider: { min: 0, max: 100, step: 10 },
                    }}
                    mode={mode}
                    onFormikFieldValueChange={(name, value) => {
                      handleSootAmountValueChange(value);
                    }}
                    error={
                      touched.sootAmountValue && errors.sootAmountValue != null
                    }
                    helperText={errors.sootAmountValue}
                    onBlur={() => setFieldTouched("sootAmountValue", true)}
                  />
                  <Property<number>
                    name={"ashAmountValue"}
                    label={t("Ash")}
                    type={"number"}
                    value={values.ashAmountValue ?? 100}
                    unit={values.ashAmountUnit}
                    mode={"read"}
                  />
                </Stack>
                {values.wasteComposition && (
                  <WastePieChart
                    unit={values.wasteAmountUnit}
                    wasteAmount={values.wasteAmountValue ?? 0}
                    creosoteAmount={values.creosoteAmountValue ?? 0}
                    sootAmount={values.sootAmountValue ?? 0}
                    ashAmount={values.ashAmountValue ?? 100}
                  />
                )}
              </Stack>
            ),
          },
        ]}
      />
    </PropertiesCard>
  );
};
